/**
 * RealtimeAlertsPanel - Floating panel showing real-time WebSocket alerts
 */

import { useState } from 'react';
import { useAlerts, type Alert } from '../contexts/AlertsContext';

export function RealtimeAlertsPanel() {
  const { connected, connecting, alerts, clearAlerts, dismissAlert } = useAlerts();
  const [expanded, setExpanded] = useState(false);

  const unreadCount = alerts.length;

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Alert Toasts (most recent 3) */}
      {!expanded && alerts.slice(0, 3).map((alert, index) => (
        <AlertToast
          key={alert.id}
          alert={alert}
          index={index}
          onDismiss={() => dismissAlert(alert.id)}
          onClick={() => setExpanded(true)}
        />
      ))}

      {/* Expand Button */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 px-4 py-2 bg-skop-card border border-skop-border rounded-lg hover:bg-skop-border/50 transition-colors"
      >
        <ConnectionIndicator connected={connected} connecting={connecting} />
        <span className="text-sm text-skop-text-secondary">Live</span>
        {unreadCount > 0 && (
          <span className="px-2 py-0.5 text-xs bg-skop-accent text-black rounded-full font-medium">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
        <svg
          className={`w-4 h-4 text-skop-text-secondary transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      </button>

      {/* Expanded Panel */}
      {expanded && (
        <div className="absolute bottom-12 right-0 w-80 max-h-96 bg-skop-card border border-skop-border rounded-lg shadow-xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-skop-border">
            <h3 className="font-medium text-skop-text">Real-time Alerts</h3>
            {alerts.length > 0 && (
              <button
                onClick={clearAlerts}
                className="text-xs text-skop-text-secondary hover:text-skop-text"
              >
                Clear all
              </button>
            )}
          </div>

          {/* Alerts List */}
          <div className="overflow-y-auto max-h-72">
            {alerts.length === 0 ? (
              <div className="px-4 py-8 text-center text-skop-text-secondary text-sm">
                No alerts yet
              </div>
            ) : (
              alerts.map(alert => (
                <AlertItem
                  key={alert.id}
                  alert={alert}
                  onDismiss={() => dismissAlert(alert.id)}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ConnectionIndicator({ connected, connecting }: { connected: boolean; connecting: boolean }) {
  if (connecting) {
    return <span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" />;
  }
  if (connected) {
    return <span className="w-2 h-2 bg-green-500 rounded-full" />;
  }
  return <span className="w-2 h-2 bg-red-500 rounded-full" />;
}

interface AlertToastProps {
  alert: Alert;
  index: number;
  onDismiss: () => void;
  onClick: () => void;
}

function AlertToast({ alert, index, onDismiss, onClick }: AlertToastProps) {
  const typeColors = {
    price_alert: 'border-l-yellow-500',
    whale_activity: 'border-l-blue-500',
    new_token: 'border-l-green-500',
    graduation: 'border-l-purple-500',
  };

  return (
    <div
      className={`
        mb-2 w-72 bg-skop-card border border-skop-border border-l-4 ${typeColors[alert.type]}
        rounded-lg shadow-lg cursor-pointer transform transition-all duration-300
        hover:scale-[1.02]
      `}
      style={{
        opacity: 1 - index * 0.2,
        transform: `translateY(${index * -8}px) scale(${1 - index * 0.02})`,
        animation: 'slideIn 0.3s ease-out',
      }}
      onClick={onClick}
    >
      <div className="px-3 py-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-skop-text truncate">{alert.title}</p>
            <p className="text-xs text-skop-text-secondary truncate">{alert.message}</p>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDismiss();
            }}
            className="text-skop-text-secondary hover:text-skop-text"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

interface AlertItemProps {
  alert: Alert;
  onDismiss: () => void;
}

function AlertItem({ alert, onDismiss }: AlertItemProps) {
  const typeIcons = {
    price_alert: (
      <svg className="w-5 h-5 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
      </svg>
    ),
    whale_activity: (
      <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
      </svg>
    ),
    new_token: (
      <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
      </svg>
    ),
    graduation: (
      <svg className="w-5 h-5 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
      </svg>
    ),
  };

  const timeAgo = getTimeAgo(alert.timestamp);

  return (
    <div className="flex items-start gap-3 px-4 py-3 border-b border-skop-border/50 hover:bg-skop-border/20 transition-colors">
      <div className="flex-shrink-0 mt-0.5">
        {typeIcons[alert.type]}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-skop-text">{alert.title}</p>
        <p className="text-xs text-skop-text-secondary mt-0.5">{alert.message}</p>
        <p className="text-xs text-skop-text-muted mt-1">{timeAgo}</p>
      </div>
      <button
        onClick={onDismiss}
        className="flex-shrink-0 text-skop-text-secondary hover:text-skop-text"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

function getTimeAgo(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);

  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}
